import { useState, useRef } from 'react';
import { X, FileText, Loader2, AlertCircle, Sparkles } from 'lucide-react';
import { parseRecipeText } from '../utils/recipeImport';

export function TextRecipeForm({ 
  isOpen, 
  onClose, 
  onImportSuccess, 
  userId,
}) {
  const [text, setText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const summaryRef = useRef(null);

  const focusSummary = () => {
    requestAnimationFrame(() => {
      summaryRef.current?.focus();
    });
  };

  const clearFieldError = () => {
    setFieldErrors((prev) => {
      if (!prev.text) return prev;
      const next = { ...prev };
      delete next.text;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const trimmedText = text.trim();

    if (!trimmedText) {
      const message = 'Recipe text is required.';
      setFieldErrors({ text: message });
      setError(message);
      focusSummary();
      return;
    }

    if (trimmedText.length < 50) {
      const message = 'Please paste more content. The text should include recipe name, ingredients, and instructions.';
      setFieldErrors({ text: message });
      setError(message);
      focusSummary();
      return;
    }

    setIsLoading(true);

    try {
      const result = await parseRecipeText(trimmedText, userId);

      if (result.success && result.recipe) {
        onImportSuccess(result.recipe);
        handleClose();
      } else {
        setError(result.error || 'Failed to parse recipe text');
      }
    } catch (err) {
      setError('An unexpected error occurred while parsing the recipe.');
      console.error('Recipe text parsing error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setText('');
    setError(null);
    setFieldErrors({});
    setIsLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-card text-foreground rounded-3xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)] flex-shrink-0">
          <h2 className="text-xl">Import from Text</h2>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-[color-mix(in srgb,var(--muted) 85%, var(--foreground) 15%)] transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col" noValidate>
          <div className="flex-1 overflow-y-auto px-6 py-6">
              {Object.keys(fieldErrors).length > 0 && (
                <div
                  ref={summaryRef}
                  tabIndex={-1}
                  role="alert"
                  aria-live="assertive"
                  className="mb-4 rounded-2xl border border-[#B85A5A]/30 bg-[#FEF5F5] px-4 py-3 outline-none focus:ring-2 focus:ring-[#B85A5A]"
                >
                  <div className="flex items-start gap-2 text-[#B85A5A]">
                    <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#B85A5A]" />
                    <div className="space-y-1 text-sm">
                      <p className="font-medium">Please fix the highlighted field below.</p>
                      {fieldErrors.text && <p>{fieldErrors.text}</p>}
                    </div>
                  </div>
                </div>
              )}
              
           {/* Instructions */}
            <div className="mb-4 p-4 bg-muted rounded-xl">
              <div className="flex items-start gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-primary mt-0.5" />
                <h4 className="text-sm">How to use</h4>
              </div>
              <ul className="text-xs text-muted-foreground space-y-1 ml-6 list-disc">
                <li>Copy the entire recipe text from any source (website, document, email)</li>
                <li>Include the recipe name, ingredients list, and cooking instructions</li>
                <li>The parser will automatically detect and structure the recipe</li>
                <li>You can edit everything before saving</li>
              </ul>
            </div>

            {/* Text Input */}
            <div className="mb-4">
              <label htmlFor="recipe-text" className="block text-sm text-foreground mb-2">
                Recipe Text *
              </label>
              <textarea
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  clearFieldError();
                }}
                placeholder="Paste your recipe text here...&#10;&#10;Include the recipe name, ingredients with quantities, and cooking instructions. Our AI will extract and format everything for you."
                disabled={isLoading}
                aria-invalid={!!fieldErrors.text}
                aria-describedby={fieldErrors.text ? 'recipe-text-error' : undefined}
                className={`w-full px-4 py-3 bg-input rounded-xl border-2 border-transparent focus:border-primary focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed resize-none min-h-[300px] text-foreground placeholder-muted-foreground ${fieldErrors.text ? 'ring-2 ring-[#B85A5A]' : ''}`}
              />
              {fieldErrors.text && (
                <div id="recipe-text-error" className="mt-2 flex items-center gap-1.5 text-sm text-[#B85A5A]">
                  <AlertCircle className="w-4 h-4 text-[#B85A5A]" />
                  <span>{fieldErrors.text}</span>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                {text.length} characters {text.length >= 50 ? '✓' : `(minimum 50)`}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="px-6 py-4 border-t border-[var(--border)] flex-shrink-0">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="flex-1 py-3 px-6 bg-card border-2 border-[var(--border)] text-muted-foreground rounded-full hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 py-3 px-6 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Importing...</span>
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5" />
                    <span>Import Recipe</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
